import styled from "styled-components";
import React, { useCallback, useContext, useEffect, useState } from "react";
import AppContext from "AppContext";
import { supabase } from "supabaseClient";
import { Filter } from "pages/dashboard/profiles/profilefilters";
import { Avatar } from "pages/dashboard/profiles/profilerow";
import WaitlistSplitButton from "./WaitlistSplitButton";

export const ProfileCard = ({ content, profiles, setProfiles, avatarColor, statusColors }) => {
  const { platformUser } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showSitePreview, setShowSitePreview] = useState(false);
  const [showWaitlistDropdown, setShowWaitlistDropdown] = useState(false);

  // Add click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.waitlist-dropdown')) {
        setShowWaitlistDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const updateProfile = useCallback(async (profileId, organizationId, newStatus) => {
    if (!platformUser) {
      console.error("User ID is missing");
      return;
    }

    const token = await supabase.auth.getSession().then(({ data }) => data.session?.access_token);

    if (!token) {
      console.error("Failed to fetch token");
      return;
    }

    setLoading(true);
    setError(null);

    const BASE_URL = process.env.REACT_APP_ENV === 'development'
      ? process.env.REACT_APP_BACKEND_URL_LOCAL
      : process.env.REACT_APP_BACKEND_URL;

    try {
      const response = await fetch(`${BASE_URL}/org-profiles-update`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          profile_id: profileId,
          organization_id: organizationId,
          status: newStatus,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update profile status");
      }

      const updatedData = await response.json();

      // Handle team creation response
      if (updatedData.teamError) {
        console.error("Team creation failed:", updatedData.teamError);
        setError("Profile updated but team creation failed: " + updatedData.teamError);
      } else if (updatedData.team) {
        console.log("Team created successfully:", updatedData.team);
      }

      // Optimistically update the UI
      setProfiles((prevProfiles) =>
        prevProfiles.map((profile) =>
          profile.id === profileId
            ? { ...profile, status: newStatus }
            : profile
        )
      );

      return updatedData;
    } catch (err) {
      console.error("Update error:", err);
      setError(err.message || "An unexpected error occurred");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [platformUser]);

  const handleUpdateStatus = (profileId, organizationId, newStatus) => {
    updateProfile(profileId, organizationId, newStatus);
  };

  const isValidUrl = useCallback((url) => {
    if (!url) return false;

    try {
      // Add protocol if missing
      const urlWithProtocol = url.match(/^https?:\/\//) ? url : `https://${url}`;
      new URL(urlWithProtocol);
      return urlWithProtocol;
    } catch (e) {
      return false;
    }
  }, []);

  // Get valid URL once when component mounts or when profileUrl changes
  const validUrl = isValidUrl(content?.profiledata?.profileUrl);

  // Reset preview state when content changes
  useEffect(() => {
    setShowSitePreview(false);
  }, [content]);

  const previewSite = (url) => {
    const validUrl = isValidUrl(url);
    console.warn(validUrl)
    if (validUrl) {
      setShowSitePreview((prev) => (prev === validUrl ? null : validUrl));
    }
  };

  useEffect(() => {
    // console.log("Updated showSitePreview:", showSitePreview);
  }, [showSitePreview]);

  const getButtonText = () => {
    // If current status matches the button state, show the status
    if (content?.status === "banned") return "Banned";
    if (content?.status === "rejected") return "Rejected";
    if (content?.status === "waitlist") return "Waitlisted";

    // Otherwise show the action that will be performed
    return "Waitlist ?";
  };

  return (
    <ProfileCardContainer>
      <ProfileCardHeader>
        <CardHeaderTop>
          <Avatar $color={avatarColor} className="Avatar">
            {(content?.profiledata?.userFname?.charAt(0) || '') + (content?.profiledata?.userLname?.charAt(0) || '')}
          </Avatar>
          <ProfileMeta className="ProfileMeta">
            <div className="orgName">{content?.profiledata.orgName}</div>
            {validUrl && (
              <span onClick={() => previewSite(content?.profiledata.profileUrl)} className="website">
                {showSitePreview ? "Close Website" : "Preview Site"}
              </span>
            )}
          </ProfileMeta>
        </CardHeaderTop>
        {showSitePreview && (
          <CardOverlay>
            <iframe
              src={showSitePreview}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                borderRadius: "3px",
              }}
              title="Site Preview"
              sandbox="allow-scripts allow-same-origin allow-popups"
            />
          </CardOverlay>
        )}
      </ProfileCardHeader>
      <TagsContainer>
        <Tag>
          {content?.profiledata.userLocation}
        </Tag>
        {content?.profiledata?.gender === "Female" && <Tag className="wf">Women Founder</Tag>}
        <Tag className="status">
          <span className="status-dot" style={{ backgroundColor: statusColors[content?.status?.toLowerCase()] }}></span>
          {content?.status}
        </Tag>
      </TagsContainer>
      <ProfileCardContent>
        <TeamContainer>
          <h5>Team</h5>
          <TeamMember>
            <span className="name">{content?.profiledata.userFname} {content?.profiledata.userLname}</span>
            <span className="email">{content?.email || "No email"}</span>
          </TeamMember>
        </TeamContainer>
        <SupportContainer>
          <h5>Signed up for:</h5>
          <SupportTags>
            {content?.profiledata?.supportOptions &&
              Object.entries(content?.profiledata.supportOptions)
                // Sort true values first
                .sort(([, valueA], [, valueB]) => (valueA === valueB ? 0 : valueA ? -1 : 1))
                .map(([key, value]) => (
                  <SupportTag key={key} className={value ? "" : "false"}>
                    {key}
                  </SupportTag>
                ))}
          </SupportTags>
        </SupportContainer>
        {content?.profiledata?.orgDescription?.length > 0 && (
          <DescriptionContainer>
            <h5>Description:</h5>
            <ProfileDescription>
              {content?.profiledata.orgDescription}
            </ProfileDescription>
          </DescriptionContainer>
        )}
      </ProfileCardContent>
      {/* <Disclaimer>Approving users will provide them access to their Participant Dashboard.</Disclaimer> */}
      <ProfileCardButtonsContainer>
        <WaitlistButtonContainer className="waitlist-dropdown">
          <WaitlistSplitButton
            content={content}
            onUpdateStatus={handleUpdateStatus}
          />
        </WaitlistButtonContainer>
      </ProfileCardButtonsContainer>
    </ProfileCardContainer>
  );
}

const WaitlistButtonContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  z-index: 1;
`;

const ProfileCardButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 13px 10px; 
  margin: 0px -15px;
  width: calc(100% + 30px);
  color: white;
  bottom: -13px;
  box-sizing: border-box;
  position: absolute;
  box-shadow: 0px 1px 3px 2px rgba(0, 0, 0, 0.05);
  background: white;
      background: white;
    height: 65px;
`;

const ProfileCardContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 5px;
  width: 100%;
  height: 100%;
`;

const ProfileCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  padding-top: 15px;
  padding-bottom: 40px;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
`;

const ProfileCardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 10px 10px 0px;
  box-sizing: border-box;
  // background: #f7f7f7;
  border-radius: 3px;
  .Avatar {
    padding: 20px;
    font-size: 14px;
  }
`;

const CardHeaderTop = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const ProfileMeta = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 3px 4px;
  .orgName {
    font-size: 14px;
    font-weight: 900;
    color: #000;
  }
  .website {
    font-size: 12px;
    color: #0095D4;
    margin: 0px;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
  }
`;

const ProfileDescription = styled.div`
  color: #000;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  font-family: 'PT Serif',
  line-height: 21x;
  letter-spacing: 0.09px;
  box-sizing: border-box;
  border-radius: 3px;
  background: #f7f7f7;
  margin: 5px -5px;
  padding: 15px 15px 15px 25px;
  overflow-y: auto;
  height: fit-content;
  margin-left: -20px;
`;

const TagsContainer = styled.div`
  padding: 0px 8px 0px;
  display: flex;
  flex-direction: row;
  gap: 10px;
  flex-wrap: wrap;
`;

const Tag = styled(Filter)`
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 3px;
  &.wf {
    background: green;
    color: white;
    box-shadow: none;
  }
  &.status {
    gap: 5px;
    text-transform: uppercase;
    font-size: 11px;
    .status-dot {
      width: 11px;
      height: 11px;
      border-radius: 50%;
    }
  }
`;

const TeamContainer = styled.div`
  padding: 0px 11px 0px;
  display: flex;
  flex-direction: column;
  h5 {
    margin-top: 5px;
    margin-bottom: 10px;
    color:rgb(138, 138, 138);
  }
`;

const TeamMember = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  .name {
    color: #000;
    font-size: 13px;
    letter-spacing: 0.13px;
    font-weight: 700;
    text-transform: capitalize;
  }
  .email {
    color: #737373;
    text-decoration-line: underline;
    font-size: 12px;
    letter-spacing: 0.12px;
    display: none;
  }
`;

const SupportContainer = styled(TeamContainer)``;

const SupportTags = styled(TagsContainer)`
  display: flex;
  flex-direction: row;
  gap: 5px;
  margin: 0px -5px;
  padding: 0px;
`;

const SupportTag = styled(Tag)`
  border-radius: 20px;
  padding: 5px 15px;
  text-transform: capitalize;
  font-weight: 700;
  background: #3C3C3C;
  color: white;
  &.false {
    background: white;
    color: #8C8C8C99;
  }
`;

const DescriptionContainer = styled(TeamContainer)`
  h5 {
    margin-top: 5px;
    margin-bottom: 5px;
    color:rgb(138, 138, 138);
  }
    margin-bottom: 20px;
`;

const Disclaimer = styled.div`
  color: #888;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 0.11px;
  margin-top: auto;
  margin-bottom: 80px; 
  overflow: visible;
  padding: 0px 10px;
`;

const CardOverlay = styled.div`
  position: absolute;
  bottom: -5px;
  left: -8px;
  width: 105%;
  height: calc(100% - 60px);
  margin: 0 auto;
  background: white;
  opacity: 1;
  z-index: 1;
  border-radius: 10px;
  box-shadow: 0px 0px 5px 2px rgba(0, 0, 0, 0.1);
`;